import React from 'react';
import styled from 'styled-components';
import { createSelector } from 'reselect';

import { useAppSelector, useAppDispatch, RootState } from '@/store';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { betsSlipSetBetBuilderBets } from '../../../modules/bets/store/actions/betsSlip';
import { formatDate, formatOddName, formatBetTitle } from '@/components/modules/bets/utils/functions';
import {
  EventWithMarkets,
  clearFetchedEventsWithMarkets,
  fetchEventsWithMarkets,
} from '@/modules/bets/utils/eventsFetcher';
import crests from '../../../modules/bets/utils/crests';

//import './index.scss';

type MultiBetBuilderBoostersProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const emptyArray: any = [];
const buildMultiBetBuilders = (cfg: any, pm: any, language: string) => {
  if (!('multiBetBuilder' in cfg)) {
    return emptyArray;
  }

  if (!Array.isArray(cfg.multiBetBuilder)) {
    return emptyArray;
  }

  const multiBetBuilder = [];

  for (let i = 0; i < cfg.multiBetBuilder.length; i++) {
    const dmb = { ...JSON.parse(JSON.stringify(cfg.multiBetBuilder[i])), endOffer: Infinity };

    if (!(dmb.eventId in pm)) {
      continue;
    }

    const m = pm[dmb.eventId];

    if (!(m.matchBets && Array.isArray(m.matchBets))) {
      continue;
    }

    dmb.number = i;
    dmb.endOffer = m.matchDateTime;
    let hasError = false;

    dmb.stakes.forEach((s: any, index: number) => {
      let stake = null;
      for (const mb of m.matchBets) {
        for (const mbo of mb.mbOutcomes) {
          if (mbo.idMbo === s.stakeId) {
            stake = { ...mbo, _original: { ...s } };
            stake.idMb = mb.idMb;
            stake.betDisplayName = formatBetTitle(mb, m, null, language);
            stake.mboDisplayName = formatOddName(mb.idBet, mbo, m, null, language);
            stake.idMatch = m.idMatch;
            stake.idSport = m.idSport;
            stake.mType = m.mType;
            break;
          }
        }
      }

      if (!stake && s.eventPeriodId && m.periods?.length) {
        for (const period of m.periods) {
          for (const mb of period.matchBets) {
            for (const mbo of mb.mbOutcomes) {
              if (mbo.idMbo === s.stakeId) {
                stake = { ...mbo, _original: { ...s } };
                stake.betDisplayName = formatBetTitle(mb, period, null, language);
                stake.idMb = mb.idMb;
                stake.mboDisplayName = formatOddName(mb.idBet, mbo, m, period, language);
                stake.idMatch = m.idMatch;
                stake.idSport = m.idSport;
                stake.mType = m.mType;
                break;
              }
            }
          }
        }
      }

      if (!stake) {
        hasError = true;
        return;
      }

      dmb.stakes[index] = stake;
    });

    if (hasError) {
      continue;
    }

    dmb.match = m;
    dmb.matchFormatted = {
      sportName: m.sportName,
      team1LogoUrl: crests(m.team1Name, m, 1),
      team1Name: m.team1Name,
      team2LogoUrl: crests(m.team2Name, m, 2),
      team2Name: m.team2Name,
      matchDateTimeString: formatDate(m.matchDateTime, language),
    };

    multiBetBuilder.push(dmb);
  }

  multiBetBuilder.sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0));

  return multiBetBuilder;
};

export const getPrematchMatches = (state: RootState) => state.bets.prematch.matches;
export const getConfig = (state: RootState) => state.bets.config;
export const getLanguage = (state: RootState) => state.application.language;

export const getMultiBetBuilders = createSelector([getConfig, getPrematchMatches, getLanguage], buildMultiBetBuilders);

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const MultiBetBuilderBoosters = (componentProps: MultiBetBuilderBoostersProps) => {
  let props = componentProps;
  let isVisible = true;

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();
  const multiBetBuilders = useAppSelector((state) => getMultiBetBuilders(state));

  /*
  const isWinnerFun = useAppSelector((state) => state.bets.app.isWinnerFun);
  const prematchBets = useAppSelector(
    (state) => state.bets.betsSlip.tickets?.[state.bets.betsSlip.currentTicket]?.prematch?.selected,
  );
  const liveBets = useAppSelector(
    (state) => state.bets.betsSlip.tickets?.[state.bets.betsSlip.currentTicket]?.live?.selected,
  );
  */

  const prematchMatches: any = useAppSelector((state) => state.bets.prematch.matches);
  const fullPrematchStateLoaded = useAppSelector((state) => state.bets.prematch.fullStateLoaded);

  const [selected, setSelected] = React.useState(-1);
  const [expanded, setExpanded] = React.useState<{ [key: number]: boolean }>({});

  const configMultiBetBuilderLoaded: any = useAppSelector((state) => state.bets.config.multiBetBuilderLoaded);
  const configMultiBetBuilder: any = useAppSelector((state) => state.bets.config.multiBetBuilder);
  const stateUpdated: any = useAppSelector((state) => state.bets.prematch.stateUpdated);

  const doneFetch = React.useRef(-1);

  const cardType = props?.properties?.cardType ?? 'all';

  React.useEffect(() => {
    //console.log('MultiBetOfTheDate[useEffect]: stateUpdated', stateUpdated);
    if (doneFetch.current != stateUpdated && fullPrematchStateLoaded && configMultiBetBuilder?.length) {
      doneFetch.current = stateUpdated;

      const eventsToLoad: { [index: string]: EventWithMarkets } = {};
      for (const dmb of configMultiBetBuilder) {
        let ev: any;
        if (!(dmb.eventId in eventsToLoad)) {
          ev = eventsToLoad[dmb.eventId] = {
            id: dmb.eventId,
            mType: 'prematch',
          };
        } else {
          ev = eventsToLoad[dmb.eventId];
        }

        dmb.stakes.forEach((st: any) => {
          if (st.eventPeriodId) {
            if (!ev.periods) ev.periods = [];
            const exists = ev.periods?.findIndex((e: any) => e.id === st.eventPeriodId);
            if (exists === -1) {
              ev.periods.push({
                id: st.eventPeriodId,
                marketIds: [st.betId],
              });
            } else {
              ev.periods[exists].marketIds.push(st.betId);
            }
          } else {
            if (!ev.marketIds) ev.marketIds = [];

            if (ev.marketIds && ev.marketIds.indexOf(st.betId) === -1) {
              ev.marketIds.push(st.betId);
            }
          }
        });
      }

      const events: any = Object.keys(eventsToLoad);

      //console.log('MultiBetOfTheDate[useEffect]', configMultiBetBuilder, eventsToLoad);

      if (events.length) {
        clearFetchedEventsWithMarkets(events);
        fetchEventsWithMarkets({ events: Object.values(eventsToLoad) });
      }
    }
  }, [stateUpdated, configMultiBetBuilder, prematchMatches, fullPrematchStateLoaded]);

  /*
  React.useEffect(() => {
    const setTicket = false;

    if (!isWinnerFun && prematchBets.length && !liveBets.length) {
      const exists: any = {};

      prematchBets.forEach((bet: any) => {
        if (bet?.bb?.info?.cardId && bet?.bb?.info?.cardType) {
          exists[`${bet?.bb?.info?.cardType}-${bet?.bb?.info?.cardId}`] = true;
        }
      });

      multiBetBuilders.find((multiBet: any) => {
        if (exists[`${multiBet.cardType}-${multiBet.id}`]) {
          setSelected(multiBet.number);
          return true;
        }
        return false;
      });
    }

    if (!setTicket) {
      setSelected(-1);
    }
  }, [multiBetBuilders, isWinnerFun, liveBets, prematchBets]);
  */

  const onMultiBetBuilderClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e?.stopPropagation();
      if (e?.currentTarget?.dataset?.index) {
        const index = parseInt(e.currentTarget.dataset.index, 10);
        if (multiBetBuilders?.length) {
          const multiBet = multiBetBuilders.find((mb: any) => mb.number === index);
          if (multiBet) {
            const betbuilder = {
              idMatch: multiBet.eventId,
              mType: multiBet.match.mType,
              bets: multiBet.stakes.map((s: any) => ({
                idSport: s.idSport,
                idMatch: s.idMatch,
                mType: s.mType,
                idBet: s.idBet,
                idMb: s.idMb,
                idBo: s.idBo,
                idMbo: s.idMbo,
                mBoActive: s.mBoActive,
                betTitle: s.betDisplayName,
                oddName: s.mboDisplayName,
              })),
              bbInfo: {
                cardId: multiBet.id,
                cardType: multiBet.cardType,
                bbf: String(multiBet.boostedFactor),
                bbsq: 0,
                mbbf: 0,
                ssec: [],
                totalBets: multiBet.stakes.length,
              },
            };

            dispatch(
              betsSlipSetBetBuilderBets(betbuilder.idMatch, betbuilder.mType, betbuilder.bets, betbuilder.bbInfo),
            );
          }
        }
      }
    },
    [multiBetBuilders, dispatch],
  );

  const contextValue = React.useMemo(() => {
    let builders = multiBetBuilders;
    if (props.properties.idMatch) {
      builders = multiBetBuilders.filter((mb: any) => mb.eventId === props.properties.idMatch);
    }

    if (['single', 'betBuilder'].indexOf(cardType) !== -1) {
      builders = builders.filter((mb: any) => mb.cardType === cardType);
    }

    return {
      multiBetBuilders: builders,
      onMultiBetBuilderClick,
      selectedNumber: selected,
      expanded,
      parentContext: dataElementContext,
    };
  }, [
    multiBetBuilders,
    onMultiBetBuilderClick,
    selected,
    expanded,
    props.properties.idMatch,
    dataElementContext,
    cardType,
  ]);

  if (configMultiBetBuilderLoaded && contextValue?.multiBetBuilders?.length === 0) {
    return null;
  }
  if (!props.properties?.showSkeleton && contextValue?.multiBetBuilders?.length === 0) {
    return null;
  }
  if (!isVisible) return null;

  //console.log('MultiBetBuilderBoosters[context]', contextValue, multiBetBuilders);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default MultiBetBuilderBoosters;
